interface Sport {
  id: string
  name: string
  name_full: string
}

interface GenericSport {
  id: string
  name: string
  background_web_image: string
  background_mobile_image: string
}

export interface MappedSport {
  sport_id: string
  generic_sport_id: string | null
  name_full: string
  name: string
  is_generic: boolean
}

interface SportsResult {
  sports: MappedSport[]
  findSport: (sportId: string, genericSportId: string) => MappedSport | null
}

/**
 * Maps sports and generic sports to a new format
 * @param sports - Array of Sports Objects
 * @param genericSports - Array of Generic Sports Objects
 * @returns An array of sports with the new format
 * To get the params, use the following:
 * import { getAllGenericSportProperties } from '@sportninja/common/selectors/types'
 * import { getSports } from '@sportninja/common/selectors/types'
 * sports: Object.values(getSports(state)),
 * genericSports: getAllGenericSportProperties(state),
 *
 */
export const useSports = (
  sports: Sport[],
  genericSports: GenericSport[]
): SportsResult => {
  if (!sports || !genericSports) {
    return {
      sports: [],
      findSport: () => null,
    }
  }
  if (sports.length === 0 || genericSports.length === 0) {
    return {
      sports: [],
      findSport: () => null,
    }
  }

  const mappedSports = sports
    .filter((sport) => {
      // Dont add generic
      return sport?.name !== 'generic'
    })
    .map((sport) => {
      return {
        sport_id: sport.id,
        generic_sport_id: null,
        name_full: sport.name_full,
        name: sport.name,
        is_generic: false,
      }
    })

  const mappedGenericSports = genericSports.map((sport) => {
    const generic = sports.find((s) => s.name === 'generic')
    if (!generic) {
      return null
    }
    return {
      sport_id: generic?.id || '',
      generic_sport_id: sport.id,
      name_full: sport.name,
      name: sport.name,
      is_generic: true,
    }
  })

  const mergedSports = [
    ...mappedSports,
    ...mappedGenericSports,
  ] as MappedSport[]

  const findSport = (
    sportId: string,
    genericSportId: string
  ): MappedSport | null => {
    if (!sportId) {
      return null
    }

    if (genericSportId) {
      return (
        mergedSports.find(
          (sport) => sport?.generic_sport_id === genericSportId
        ) || null
      )
    }
    return mergedSports.find((sport) => sport?.sport_id === sportId) || null
  }

  return {
    sports: mergedSports,
    findSport,
  }
}
